import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmtError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Reveal } from "../components/Reveal";

const AuthPage = () => {
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [isArtist, setIsArtist] = useState(false);
  const [artistName, setArtistName] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const { setUser } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const payload = mode === "register"
        ? { email, password, name, account_type: isArtist ? "artist" : "client", artist_name: artistName }
        : { email, password };
      const { data } = await api.post(`/auth/${mode === "register" ? "register" : "login"}`, payload);
      setUser(data);
      if (data.role === "artist") {
        toast.success("Compte artiste créé — en attente de validation par le studio");
        navigate("/admin");
      } else {
        toast.success(`Bienvenue ${data.name || data.email}`);
        navigate("/compte");
      }
      return;
    } catch (err) {
      setError(fmtError(err));
    } finally {
      setBusy(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/compte";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <main data-testid="auth-page" className="flex min-h-screen items-center justify-center px-5 pb-16 pt-28">
      <Reveal className="w-full max-w-md">
        <div className="border border-white/[0.08] bg-surface p-8 sm:p-10">
          <p className="text-xs uppercase tracking-[0.35em] text-amber-500">Espace client</p>
          <h1 className="mt-3 font-display text-3xl font-black tracking-tight">
            {mode === "login" ? "Bon retour" : "Créer un compte"}
          </h1>

          <div className="mt-8 grid grid-cols-2 border border-white/10 text-xs uppercase tracking-[0.2em]" data-testid="auth-mode-switch">
            <button
              onClick={() => { setMode("login"); setError(""); }}
              data-testid="auth-tab-login"
              className={`py-3 transition-colors ${mode === "login" ? "bg-amber-500 font-bold text-black" : "text-zinc-400 hover:text-white"}`}
            >
              Connexion
            </button>
            <button
              onClick={() => { setMode("register"); setError(""); }}
              data-testid="auth-tab-register"
              className={`py-3 transition-colors ${mode === "register" ? "bg-amber-500 font-bold text-black" : "text-zinc-400 hover:text-white"}`}
            >
              Inscription
            </button>
          </div>

          <form onSubmit={submit} className="mt-8 space-y-5">
            {mode === "register" && (
              <div>
                <label className="mb-2 block text-[10px] uppercase tracking-[0.25em] text-zinc-500">Nom</label>
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  data-testid="auth-name"
                  className="w-full border border-white/15 bg-transparent px-4 py-3 text-sm outline-none transition-colors focus:border-amber-500"
                  placeholder="Votre nom ou société"
                />
              </div>
            )}
            <div>
              <label className="mb-2 block text-[10px] uppercase tracking-[0.25em] text-zinc-500">Email</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="auth-email"
                className="w-full border border-white/15 bg-transparent px-4 py-3 text-sm outline-none transition-colors focus:border-amber-500"
                placeholder="vous@exemple.fr"
              />
            </div>
            <div>
              <label className="mb-2 block text-[10px] uppercase tracking-[0.25em] text-zinc-500">Mot de passe</label>
              <input
                type="password"
                required
                minLength={8}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="auth-password"
                className="w-full border border-white/15 bg-transparent px-4 py-3 text-sm outline-none transition-colors focus:border-amber-500"
                placeholder="8 caractères minimum"
              />
            </div>
            {mode === "register" && (
              <>
                <label className="flex cursor-pointer items-center gap-3 border border-white/15 px-4 py-3 transition-colors hover:border-amber-500/50" data-testid="auth-artist-toggle">
                  <input
                    type="checkbox"
                    checked={isArtist}
                    onChange={(e) => setIsArtist(e.target.checked)}
                    className="h-4 w-4 accent-amber-500"
                  />
                  <span className="text-xs text-zinc-300">Je suis un artiste — je veux vendre mes musiques</span>
                </label>
                {isArtist && (
                  <div>
                    <label className="mb-2 block text-[10px] uppercase tracking-[0.25em] text-zinc-500">Nom de scène</label>
                    <input
                      value={artistName}
                      onChange={(e) => setArtistName(e.target.value)}
                      data-testid="auth-artist-name"
                      className="w-full border border-white/15 bg-transparent px-4 py-3 text-sm outline-none transition-colors focus:border-amber-500"
                      placeholder="Votre nom d'artiste public"
                    />
                    <p className="mt-2 text-[11px] leading-relaxed text-zinc-500">
                      Validation par le studio requise — l'inscription est entièrement gratuite.
                    </p>
                  </div>
                )}
              </>
            )}
            {error && (
              <p data-testid="auth-error" className="border border-red-500/40 bg-red-500/10 px-4 py-3 text-xs text-red-400">
                {error}
              </p>
            )}
            <button
              type="submit"
              disabled={busy}
              data-testid="auth-submit"
              className="w-full bg-amber-500 py-4 text-xs font-bold uppercase tracking-[0.25em] text-black transition-colors hover:bg-amber-400 disabled:opacity-50"
            >
              {busy ? "Patience…" : mode === "login" ? "Se connecter" : "Créer mon compte"}
            </button>
          </form>

          <div className="my-6 flex items-center gap-4 text-[10px] uppercase tracking-[0.25em] text-zinc-600">
            <span className="h-px flex-1 bg-white/10" /> ou <span className="h-px flex-1 bg-white/10" />
          </div>

          <button
            onClick={googleLogin}
            data-testid="auth-google"
            className="flex w-full items-center justify-center gap-3 border border-white/15 py-4 text-xs uppercase tracking-[0.25em] text-zinc-300 transition-colors hover:border-amber-500 hover:text-amber-400"
          >
            <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
              <path d="M21.35 11.1h-9.17v2.73h6.51c-.33 3.81-3.5 5.44-6.5 5.44C8.36 19.27 5 16.25 5 12c0-4.1 3.2-7.27 7.2-7.27 3.09 0 4.9 1.97 4.9 1.97L19 4.72S16.56 2 12.1 2C6.42 2 2.03 6.8 2.03 12c0 5.05 4.13 10 10.22 10 5.35 0 9.25-3.67 9.25-9.09 0-1.15-.15-1.81-.15-1.81Z" />
            </svg>
            Continuer avec Google
          </button>
        </div>
      </Reveal>
    </main>
  );
};

export default AuthPage;
